import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { Card, Row, Col, Form, Button, Spinner, Alert } from "react-bootstrap";
import axiosInstance from "../../../../services/AxiosInstance";
import MenuConfig from "../../../../constants/MenuConfig";
import PageTitle from "../../../layouts/PageTitle";
import { successAlert } from "../../../components/alert/defaultAlert";

const DAYS_OF_WEEK = [
    { key: 'MONDAY', label: 'Segunda-feira' },
    { key: 'TUESDAY', label: 'Terça-feira' },
    { key: 'WEDNESDAY', label: 'Quarta-feira' },
    { key: 'THURSDAY', label: 'Quinta-feira' },
    { key: 'FRIDAY', label: 'Sexta-feira' },
    { key: 'SATURDAY', label: 'Sábado' },
    { key: 'SUNDAY', label: 'Domingo' }
];

const BranchWorkingHours = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [branchName, setBranchName] = useState('');
    const [workingHours, setWorkingHours] = useState([]);

    // Carrega os horários de funcionamento
    useEffect(() => {
        loadWorkingHours();
        loadBranchInfo();
        // eslint-disable-next-line
    }, [id]);

    const loadBranchInfo = async () => {
        try {
            const response = await axiosInstance.get(`/main/branches/${id}`);
            setBranchName(response.data.name);
        } catch (err) {
            console.error("Erro ao carregar filial:", err);
        }
    };

    const loadWorkingHours = async (showLoading = true) => {
        try {
            if (showLoading) {
                setLoading(true);
            }
            const response = await axiosInstance.get(`/main/branch-working-hours/branch/${id}`);
            
            // Organiza os dados recebidos
            const hoursData = DAYS_OF_WEEK.map(day => {
                const found = response.data.find(h => h.dayOfWeek === day.key);
                if (found) {
                    return {
                        ...found,
                        dayLabel: day.label
                    };
                }
                // Valores padrão se não encontrar
                return {
                    branchId: parseInt(id),
                    dayOfWeek: day.key,
                    dayLabel: day.label,
                    isOpen: day.key !== 'SUNDAY',
                    openTime: day.key === 'SATURDAY' ? '08:00' : '08:00',
                    closeTime: day.key === 'SATURDAY' ? '12:00' : '18:00',
                    breakStartTime: null,
                    breakEndTime: null
                };
            });
            
            setWorkingHours(hoursData);
        } catch (err) {
            setError(t('error_loading_data'));
            console.error("Erro ao carregar horários:", err);
        } finally {
            if (showLoading) {
                setLoading(false);
            }
        }
    };

    const handleToggleDay = (index) => {
        const updated = [...workingHours];
        updated[index].isOpen = !updated[index].isOpen;
        setWorkingHours(updated);
    };

    const handleTimeChange = (index, field, value) => {
        const updated = [...workingHours];
        updated[index][field] = value || null;
        setWorkingHours(updated);
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            setError('');

            // Prepara os dados para envio
            const payload = workingHours.map(wh => ({
                id: wh.id || null,
                branchId: parseInt(id),
                dayOfWeek: wh.dayOfWeek,
                isOpen: wh.isOpen,
                openTime: wh.isOpen ? wh.openTime : null,
                closeTime: wh.isOpen ? wh.closeTime : null,
                breakStartTime: wh.isOpen ? wh.breakStartTime : null,
                breakEndTime: wh.isOpen ? wh.breakEndTime : null
            }));

            await axiosInstance.put(`/main/branch-working-hours/branch/${id}`, payload);
            successAlert(t('saved_successfully'));
            
            // Sincroniza IDs e valores retornados pelo backend sem retirar os cards da tela.
            await loadWorkingHours(false);
        } catch (err) {
            setError(err.response?.data?.message || t('error_saving_data'));
            console.error("Erro ao salvar horários:", err);
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        navigate(MenuConfig.main.branch.route);
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    return (
        <>
            <PageTitle
                motherMenu={t('main')}
                activeMenu={t('working_hours')}
                childMenu={branchName || t('branch')}
            />

            <Card>
                <Card.Header>
                    <Card.Title>{t('working_hours')}</Card.Title>
                    <p className="text-muted mb-0">
                        {t('working_hours_description')}
                    </p>
                </Card.Header>
                <Card.Body>
                    {error && (
                        <Alert variant="danger" onClose={() => setError('')} dismissible>
                            {error}
                        </Alert>
                    )}
                    <div className="working-hours-list">
                        {workingHours.map((wh, index) => (
                            <Card key={wh.dayOfWeek} className="mb-3 border">
                                <Card.Body className="py-3">
                                    <Row className="align-items-center">
                                        {/* Dia da semana e toggle */}
                                        <Col xs={12} md={3} className="mb-2 mb-md-0">
                                            <div className="d-flex align-items-center">
                                                <Form.Check
                                                    type="switch"
                                                    id={`switch-${wh.dayOfWeek}`}
                                                    checked={wh.isOpen}
                                                    onChange={() => handleToggleDay(index)}
                                                    className="me-2"
                                                />
                                                <span className={`fw-bold ${!wh.isOpen ? 'text-muted' : ''}`}>
                                                    {wh.dayLabel}
                                                </span>
                                            </div>
                                        </Col>

                                        {/* Horários */}
                                        {wh.isOpen ? (
                                            <>
                                                {/* Horário de funcionamento */}
                                                <Col xs={6} md={2} className="mb-2 mb-md-0">
                                                    <Form.Group>
                                                        <Form.Label className="small text-muted mb-1">
                                                            {t('open_time')}
                                                        </Form.Label>
                                                        <Form.Control
                                                            type="time"
                                                            value={wh.openTime || ''}
                                                            onChange={(e) => handleTimeChange(index, 'openTime', e.target.value)}
                                                            size="sm"
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col xs={6} md={2} className="mb-2 mb-md-0">
                                                    <Form.Group>
                                                        <Form.Label className="small text-muted mb-1">
                                                            {t('close_time')}
                                                        </Form.Label>
                                                        <Form.Control
                                                            type="time"
                                                            value={wh.closeTime || ''}
                                                            onChange={(e) => handleTimeChange(index, 'closeTime', e.target.value)}
                                                            size="sm"
                                                        />
                                                    </Form.Group>
                                                </Col>

                                                {/* Intervalo */}
                                                <Col xs={6} md={2} className="mb-2 mb-md-0">
                                                    <Form.Group>
                                                        <Form.Label className="small text-muted mb-1">
                                                            {t('break_start')}
                                                        </Form.Label>
                                                        <Form.Control
                                                            type="time"
                                                            value={wh.breakStartTime || ''}
                                                            onChange={(e) => handleTimeChange(index, 'breakStartTime', e.target.value)}
                                                            size="sm"
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col xs={6} md={2} className="mb-2 mb-md-0">
                                                    <Form.Group>
                                                        <Form.Label className="small text-muted mb-1">
                                                            {t('break_end')}
                                                        </Form.Label>
                                                        <Form.Control
                                                            type="time"
                                                            value={wh.breakEndTime || ''}
                                                            onChange={(e) => handleTimeChange(index, 'breakEndTime', e.target.value)}
                                                            size="sm"
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </>
                                        ) : (
                                            <Col xs={12} md={8}>
                                                <span className="text-danger">
                                                    <i className="fa fa-times-circle me-2"></i>
                                                    {t('closed')}
                                                </span>
                                            </Col>
                                        )}
                                    </Row>
                                </Card.Body>
                            </Card>
                        ))}
                    </div>

                    {/* Botões de ação */}
                    <div className="d-flex justify-content-end gap-2 mt-4">
                        <Button variant="outline-secondary" onClick={handleCancel}>
                            <i className="fa fa-arrow-left me-2"></i>
                            {t('back')}
                        </Button>
                        <Button variant="primary" onClick={handleSave} disabled={saving}>
                            {saving ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    {t('saving')}
                                </>
                            ) : (
                                <>
                                    <i className="fa fa-save me-2"></i>
                                    {t('save')}
                                </>
                            )}
                        </Button>
                    </div>
                </Card.Body>
            </Card>
        </>
    );
};

export default BranchWorkingHours;
